// frontend/src/components/projects/workspaces/EnglishProjectWorkspace.tsx
// İngilizce dersleri için Language & Creative Writing Studio

import { useState, useEffect } from 'react';

export interface EnglishVocabItem {
  word: string;
  meaning: string;
  exampleSentence: string;
}

export interface EnglishWorkData {
  topic: string;
  vocabulary: EnglishVocabItem[];
  englishText: string;
  turkishSummary: string;
  grammarFocus: string;
}

interface EnglishProjectWorkspaceProps {
  initialContent: string;
  projectTitle: string;
  projectDescription: string | null;
  onChange: (formattedText: string) => void;
  disabled?: boolean;
}

export function EnglishProjectWorkspace({
  initialContent,
  projectTitle,
  projectDescription,
  onChange,
  disabled = false,
}: EnglishProjectWorkspaceProps) {
  const [data, setData] = useState<EnglishWorkData>(() => {
    try {
      if (initialContent.startsWith('{') && initialContent.includes('vocabulary')) {
        return JSON.parse(initialContent);
      }
    } catch {
      // Fallback
    }
    return {
      topic: '',
      vocabulary: [
        { word: 'Explore', meaning: 'Keşfetmek', exampleSentence: 'We explore new planets.' },
        { word: 'Universe', meaning: 'Evren', exampleSentence: 'The universe is huge.' },
      ],
      englishText: '',
      turkishSummary: '',
      grammarFocus: '',
    };
  });

  const [activeTab, setActiveTab] = useState<'editor' | 'flashcards'>('editor');

  useEffect(() => {
    const formatted = `### 🇬🇧 ENGLISH LANGUAGE & WRITING PROJECT
**Project Title:** ${projectTitle}
**Topic / Scenario:** ${data.topic || projectTitle}
**Grammar Focus:** ${data.grammarFocus || 'General English'}

#### 1. Target Vocabulary & Phrases
${data.vocabulary.map((v, i) => `${i + 1}. **${v.word}** (${v.meaning}): "${v.exampleSentence}"`).join('\n')}

#### 2. English Text / Dialogue / Story
${data.englishText || '(No English text submitted yet)'}

#### 3. Turkish Translation & Summary
${data.turkishSummary || '(Türkçe açıklama girilmedi)'}
`;
    onChange(formatted);
  }, [data, projectTitle, onChange]);

  function updateField<K extends keyof EnglishWorkData>(key: K, value: EnglishWorkData[K]) {
    setData((prev) => ({ ...prev, [key]: value }));
  }

  function handleVocabChange(index: number, field: keyof EnglishVocabItem, val: string) {
    const newVocab = [...data.vocabulary];
    newVocab[index] = { ...newVocab[index], [field]: val };
    updateField('vocabulary', newVocab);
  }

  function addVocab() {
    updateField('vocabulary', [...data.vocabulary, { word: '', meaning: '', exampleSentence: '' }]);
  }

  function removeVocab(index: number) {
    if (data.vocabulary.length <= 1) return;
    updateField('vocabulary', data.vocabulary.filter((_, i) => i !== index));
  }

  return (
    <div className="english-workspace-container">
      {/* Sol Panel: İngilizce Çalışma Formu */}
      <div className="english-editor-panel card">
        <div className="english-panel-header">
          <div className="english-badge">🇬🇧 English Language & Creative Studio</div>
          <div className="english-tabs">
            <button
              type="button"
              className={`english-tab-btn ${activeTab === 'editor' ? 'active' : ''}`}
              onClick={() => setActiveTab('editor')}
            >
              ✍️ Write & Create
            </button>
            <button
              type="button"
              className={`english-tab-btn ${activeTab === 'flashcards' ? 'active' : ''}`}
              onClick={() => setActiveTab('flashcards')}
            >
              🃏 Flashcards & Preview
            </button>
          </div>
        </div>

        {activeTab === 'editor' ? (
          <div className="english-form-body">
            {/* Konu & Gramer Odağı */}
            <div className="english-grid-2">
              <div className="english-form-group">
                <label className="english-label">
                  <span>Topic / Theme (Konu & Tema)</span>
                </label>
                <input
                  type="text"
                  className="input"
                  placeholder="e.g. Space Travel, Daily Routine, Science Fair..."
                  value={data.topic}
                  onChange={(e) => updateField('topic', e.target.value)}
                  disabled={disabled}
                />
              </div>

              <div className="english-form-group">
                <label className="english-label">
                  <span>Grammar Focus (Kullanılan Dilbilgisi)</span>
                </label>
                <input
                  type="text"
                  className="input"
                  placeholder="e.g. Simple Present, Comparatives, Past Tense..."
                  value={data.grammarFocus}
                  onChange={(e) => updateField('grammarFocus', e.target.value)}
                  disabled={disabled}
                />
              </div>
            </div>

            {/* 1. Hedef Kelimeler (Target Vocabulary) */}
            <div className="english-form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label className="english-label" style={{ margin: 0 }}>
                  <span>1. Target Vocabulary (Hedef Kelimeler & Cümleler)</span>
                </label>
                <button type="button" className="english-add-btn" onClick={addVocab} disabled={disabled}>
                  + Add Word
                </button>
              </div>
              <div className="english-vocab-list">
                {data.vocabulary.map((item, idx) => (
                  <div key={idx} className="english-vocab-row">
                    <input
                      type="text"
                      className="input"
                      style={{ flex: '0 0 130px', fontWeight: 600 }}
                      placeholder="Word (English)"
                      value={item.word}
                      onChange={(e) => handleVocabChange(idx, 'word', e.target.value)}
                      disabled={disabled}
                    />
                    <input
                      type="text"
                      className="input"
                      style={{ flex: '0 0 130px' }}
                      placeholder="Meaning (Türkçe)"
                      value={item.meaning}
                      onChange={(e) => handleVocabChange(idx, 'meaning', e.target.value)}
                      disabled={disabled}
                    />
                    <input
                      type="text"
                      className="input"
                      style={{ flex: 1 }}
                      placeholder="Example Sentence in English..."
                      value={item.exampleSentence}
                      onChange={(e) => handleVocabChange(idx, 'exampleSentence', e.target.value)}
                      disabled={disabled}
                    />
                    {data.vocabulary.length > 1 && (
                      <button
                        type="button"
                        className="english-del-btn"
                        onClick={() => removeVocab(idx)}
                        disabled={disabled}
                        title="Delete Word"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* 2. İngilizce Metin / Diyalog / Hikaye */}
            <div className="english-form-group">
              <label className="english-label">
                <span>2. English Text / Dialogue / Story (İngilizce Metin & Diyalog)</span>
                <span className="english-hint">Write a paragraph, a dialogue between characters, or a presentation in English.</span>
              </label>
              <textarea
                className="input english-textarea"
                rows={6}
                placeholder="Write your English project text here. Use the target vocabulary and correct sentence structures..."
                value={data.englishText}
                onChange={(e) => updateField('englishText', e.target.value)}
                disabled={disabled}
              />
            </div>

            {/* 3. Türkçe Açıklama & Özet */}
            <div className="english-form-group">
              <label className="english-label">
                <span>3. Turkish Summary & Context (Türkçe Açıklama & Özet)</span>
                <span className="english-hint">Yazdığın metnin Türkçe özetini ve vermek istediğin mesajı belirt.</span>
              </label>
              <textarea
                className="input english-textarea"
                rows={3}
                placeholder="Yazdığın metnin Türkçe özetini ve ana fikrini açıkla..."
                value={data.turkishSummary}
                onChange={(e) => updateField('turkishSummary', e.target.value)}
                disabled={disabled}
              />
            </div>
          </div>
        ) : (
          <div className="english-cards-view">
            <div className="english-view-header">
              <h2>🃏 Flashcards & Project Preview</h2>
              <div className="english-view-title">{projectTitle}</div>
            </div>

            {/* Flashcard Kartları */}
            <div className="english-flashcards-grid">
              {data.vocabulary.map((voc, i) => (
                <div key={i} className="english-flashcard-item">
                  <div className="flashcard-front">
                    <span className="card-word">{voc.word || 'Word'}</span>
                    <span className="card-meaning">{voc.meaning || 'Anlam'}</span>
                  </div>
                  <div className="flashcard-example">
                    <em>"{voc.exampleSentence || 'No sentence added yet.'}"</em>
                  </div>
                </div>
              ))}
            </div>

            {/* Metin Önizleme */}
            <div className="english-preview-box">
              <h4>📜 English Story / Passage</h4>
              <p className="english-story-text">
                {data.englishText || '— No English text written yet —'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Sağ Panel: İngilizce Değerlendirme Rehberi */}
      <div className="english-side-panel">
        <div className="card english-guide-card">
          <h3>🇬🇧 English Evaluation Criteria</h3>
          <p className="english-guide-desc">
            İngilizce projelerinde kelime çeşitliliği, doğru gramer ve akıcı cümle kurgusu değerlendirilir.
          </p>
          <div className="english-checklist">
            <div className={`english-check-item ${data.vocabulary.some(v => v.word.trim()) ? 'done' : ''}`}>
              <span className="check-icon">{data.vocabulary.some(v => v.word.trim()) ? '✅' : '⚪'}</span>
              <div>
                <strong>Rich Vocabulary (Kelime Dağarcığı)</strong>
                <p>Hedef kelimeleri doğru bağlamda kullanın.</p>
              </div>
            </div>
            <div className={`english-check-item ${data.englishText.trim().length > 30 ? 'done' : ''}`}>
              <span className="check-icon">{data.englishText.trim().length > 30 ? '✅' : '⚪'}</span>
              <div>
                <strong>Accurate Grammar (Doğru Gramer)</strong>
                <p>Zaman uyumu ve cümle yapısına dikkat edin.</p>
              </div>
            </div>
            <div className={`english-check-item ${data.englishText.trim().length > 80 ? 'done' : ''}`}>
              <span className="check-icon">{data.englishText.trim().length > 80 ? '✅' : '⚪'}</span>
              <div>
                <strong>Fluency & Creativity (Akıcılık)</strong>
                <p>Akıcı bir diyalog veya yaratıcı bir hikaye kurgulayın.</p>
              </div>
            </div>
          </div>
        </div>

        {projectDescription && (
          <div className="card english-info-card">
            <h4>📌 Project Goal</h4>
            <p>{projectDescription}</p>
          </div>
        )}
      </div>
    </div>
  );
}
